import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserRoles, AppRole, ROLE_LABELS, hasRole } from '@/hooks/useUserRoles';
import { Shield, Crown, Users, Briefcase, UserCheck, Calculator, Phone, User } from 'lucide-react';

interface UserRoleAssignmentSectionProps {
  userId: string | null;
  selectedRoles: AppRole[];
  onRoleToggle: (role: AppRole) => void;
}

const ROLE_ICONS: Record<AppRole, any> = {
  'super_admin': Crown,
  'admin': Shield,
  'manager': Users,
  'sales_manager': Briefcase,
  'sales': UserCheck,
  'hr_manager': Users,
  'hr': UserCheck,
  'accountant': Calculator,
  'receptionist': Phone,
  'user': User
};

const ROLE_COLORS: Record<AppRole, string> = {
  'super_admin': '#dc2626',
  'admin': '#ea580c',
  'manager': '#d97706',
  'sales_manager': '#16a34a',
  'sales': '#22c55e',
  'hr_manager': '#0284c7',
  'hr': '#0ea5e9',
  'accountant': '#7c3aed',
  'receptionist': '#db2777',
  'user': '#64748b'
};

export const UserRoleAssignmentSection = ({ 
  userId, 
  selectedRoles, 
  onRoleToggle 
}: UserRoleAssignmentSectionProps) => {
  const { data: currentRoles, isLoading } = useUserRoles(userId || undefined);
  
  const availableRoles: AppRole[] = [
    'super_admin',
    'admin',
    'manager',
    'sales_manager',
    'sales',
    'hr_manager',
    'hr',
    'accountant',
    'receptionist',
    'user'
  ];

  return (
    <div className="space-y-4 border-t pt-4">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-slate-800">تعيين أدوار النظام (RBAC)</h3>
      </div>
      
      {currentRoles && currentRoles.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800 font-medium mb-2">الأدوار الحالية:</p>
          <div className="flex flex-wrap gap-2">
            {currentRoles.map((role) => {
              const Icon = ROLE_ICONS[role.role];
              return (
                <Badge
                  key={role.id}
                  variant="secondary"
                  className="gap-1"
                  style={{ 
                    backgroundColor: ROLE_COLORS[role.role] + '20',
                    color: ROLE_COLORS[role.role],
                    borderColor: ROLE_COLORS[role.role]
                  }}
                >
                  <Icon className="w-3 h-3" />
                  {ROLE_LABELS[role.role]}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <Label>الأدوار المتاحة</Label>
        <p className="text-sm text-muted-foreground">
          اختر الأدوار التي تريد تعيينها للموظف (يمكن اختيار أكثر من دور)
        </p>
        <div className="grid grid-cols-2 gap-3">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))
          ) : (
            availableRoles.map((role) => {
              const isSelected = selectedRoles.includes(role);
              const Icon = ROLE_ICONS[role];
              const color = ROLE_COLORS[role];
              
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => onRoleToggle(role)}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg border-2 transition-all",
                    "hover:scale-105 active:scale-95",
                    isSelected 
                      ? 'shadow-md' 
                      : 'opacity-60 hover:opacity-100'
                  )}
                  style={{ 
                    backgroundColor: isSelected ? color + '15' : '#f1f5f9',
                    borderColor: isSelected ? color : '#e2e8f0',
                    color: isSelected ? color : '#64748b'
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{ROLE_LABELS[role]}</span>
                  {isSelected && (
                    <Shield className="w-4 h-4 mr-auto" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
      
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-xs text-amber-800">
          <strong>ملاحظة:</strong> الأدوار الجديدة ستُطبق فوراً بعد الحفظ وستؤثر على الصلاحيات في جميع أنحاء النظام.
        </p>
      </div>
    </div>
  );
};
